import { Plus } from "lucide-react";
import { useCreate, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";

import { colors } from "../tags/colors";
import { TagChip } from "../tags/TagChip";
import type { Tag } from "../types";
import { CreatableCombobox } from "./CreatableCombobox";
import { useTagsByUsage } from "./useTagsByUsage";

export interface TagPickerProps {
  value: number[];
  onChange: (tagIds: number[]) => void;
}

export const TagPicker = ({ value, onChange }: TagPickerProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [create] = useCreate<Tag>();
  const { tags, refetch } = useTagsByUsage();
  const selectedTags = tags.filter((tag) => value.includes(tag.id));

  const toggle = (tagId: number) =>
    onChange(
      value.includes(tagId)
        ? value.filter((id) => id !== tagId)
        : [...value, tagId],
    );

  const handleCreate = async (name: string) => {
    try {
      const tag = await create(
        "tags",
        { data: { name, color: colors[tags.length % colors.length] } },
        { returnPromise: true },
      );
      if (!tag) return;
      await refetch();
      onChange([...value, tag.id]);
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {selectedTags.map((tag) => (
        <TagChip key={tag.id} tag={tag} onUnlink={async () => toggle(tag.id)} />
      ))}
      <CreatableCombobox
        options={tags.map((tag) => ({
          value: String(tag.id),
          label: tag.name,
          color: tag.color,
          count: tag.nb_contacts,
        }))}
        selected={value.map(String)}
        onSelect={(tagId) => toggle(Number(tagId))}
        onCreate={handleCreate}
        placeholder={translate("crm.operations.placeholders.search_tags")}
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7"
          aria-label={translate("crm.operations.fields.tags")}
        >
          <Plus />
          {selectedTags.length === 0 && translate("crm.operations.fields.tags")}
        </Button>
      </CreatableCombobox>
    </div>
  );
};
